import { createSlice } from '@reduxjs/toolkit';
import { startListening } from '../listenerMiddleware.js';
import { userManager, ensureUserManagerSettings, getPortal, hasTokenForPortal } from './userManager.js';

const SILENT_RENEW_ERROR = 'redux-oidc/SILENT_RENEW_ERROR';
const USER_EXPIRED = 'redux-oidc/USER_EXPIRED';
const USER_FOUND = 'redux-oidc/USER_FOUND';

export const authenticate = ({ matchingIdString }) => async (dispatch, getState) => {
    const { oidc } = getState();

    if (oidc.user) {
        if (matchingIdString && oidc.user.profile.sub !== matchingIdString) {
            await dispatch(signout());
            await dispatch(redirecting());
        } else if (!hasTokenForPortal()) {
            // Without the portal of this launch in the token the Learning API refuses the registration calls
            dispatch(signin());
            dispatch(redirecting());
        } else {
            await dispatch(signedIn({ user: createUser(oidc.user) }));
        }
    } else {
        dispatch(signin());
        dispatch(redirecting());
    }
};

export const signin = () => async (_, getState) => {
    const { env } = getState();
    await ensureUserManagerSettings(env.settings);

    userManager.signinRedirect({
        state: {
            url: window.location.pathname + window.location.search,
            portal: getPortal(),
        },
    })
};

// Used by the embedded components, where the hosting app owns the token.
export const signedInExistingUser = ({ user }) => async (dispatch) => {
    await dispatch(signedInExternally({ user: createUser(user) }));
};

export const signout = () => async (dispatch, getState) => {
    const { env } = getState();

    dispatch(signoutStarted());
    await ensureUserManagerSettings(env.settings);

    userManager.signoutRedirect({
        state: {
            url: window.location.pathname + window.location.search,
        }
    });
};

export const handleSilentRenewCallback = () => async (dispatch, getState) => {
    const { env } = getState();
    await ensureUserManagerSettings(env.settings);

    userManager.signinSilentCallback()
        .then(result => {
            console.log(result);
        })
        .catch(err => {
            console.error(err);
            dispatch(signInError({ error: err }));
        });
};

// A silent renew replaces oidc.user, but every API call reads its bearer token from
// the auth.user snapshot, so that copy has to follow along or we keep sending the
// token that just expired.
const tokenRenewed = ({ user }) => async (dispatch, getState) => {
    const { auth } = getState();

    if (!auth.signedIn || auth.managedExternally) {
        return;
    }

    await dispatch(signedIn({ user: createUser(user) }));
};

// The token is gone and cannot be recovered silently: it expired or the silent
// renew failed. Drop the dead token and sign in again.
export const sessionLost = () => async (dispatch, getState) => {
    const { auth } = getState();

    if (!auth.signedIn || auth.managedExternally || auth.signingOut) {
        return;
    }

    // One atomic transition: clearing signedIn on its own leaves a state in which
    // sdb-route dispatches authenticate() and redirects a second time.
    dispatch(sessionExpired());
    dispatch(signin());
};

const authSlice = createSlice({
    name: 'auth',
    initialState: {
        redirecting: false,
        signedIn: false,
        signingOut: false,
        managedExternally: false,
        user: null,
    },
    reducers: {
        redirecting: (state) => {
            state.redirecting = true;
            state.signedIn = false;
        },
        signedIn: (state, action) => {
            const { payload } = action;

            state.redirecting = false;
            state.signedIn = true;
            state.managedExternally = false;
            state.user = { ...payload.user };
        },
        signedInExternally: (state, action) => {
            const { payload } = action;

            state.redirecting = false;
            state.signedIn = true;
            state.managedExternally = true;
            state.user = { ...payload.user };
        },
        signoutStarted: (state) => {
            state.signingOut = true;
        },
        sessionExpired: (state) => {
            state.redirecting = true;
            state.signedIn = false;
            state.user = null;
        },
        signedOut: (state) => {
            state.redirecting = false;
            state.signedIn = false;
            state.user = null;
        },
        signInError: (state) => {
            state.redirecting = false;
            state.signedIn = false;
            state.user = null;
            state.error = true;
        },
    },
});

const createUser = (oidcUser) => {
    const {
        id_token,
        access_token,
        scope,
        session_state,
        token_type,
        profile,
        expires_at
    } = oidcUser;

    return {
        id_token,
        access_token,
        scope,
        session_state,
        token_type,
        profile,
        expires_at
    };
};

export const {
    redirecting,
    signedIn,
    signedInExternally,
    signoutStarted,
    sessionExpired,
    signedOut,
    signInError
} = authSlice.actions;

export default authSlice.reducer;

// The missing wiring: keep the auth.user snapshot in sync with a renewed token.
startListening({
    type: USER_FOUND,
    effect: async (action, { dispatch }) => {
        await dispatch(tokenRenewed({ user: action.payload }));
    },
});

// Without this the auth slice ignores every failure, auth.signedIn stays true and
// the player never redirects to the login page. Only the two token lifetime events
// belong here - a sign out is left to the signout flow (see oidcEvents.js).
startListening({
    predicate: (action) => action.type === USER_EXPIRED
        || action.type === SILENT_RENEW_ERROR,
    effect: async (_, { dispatch }) => {
        await dispatch(sessionLost());
    },
});
